import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationPaymentIntegrationBrandingImageCacheKey,
    longTermCacheStaleTime,
    onUpdateOrganizationPaymentIntegrationBranding,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationPaymentIntegrationBranding', {
    failedToLoadBranding: 'Failed to load branding',
    successFullyUpdatedBranding: 'Successfully updated branding',
    failedToUpdateBranding: 'Failed to update branding',
    partialError: 'The {target} failed to update.  Ensure the images meet the stated requirements.',
    icon: 'icon',
    logo: 'logo',
    iconAndLogo: 'icon and logo',
});

export type UseOrganizationPaymentIntegrationBrandingArgs = {
    onUpdatedBranding?: () => void;
};

export type UseOrganizationPaymentIntegrationBrandingReturnValue = {
    updateBranding: (args: APITypes.organizationService.UpdateOrganizationPaymentIntegrationImagesRequest) => void;
    isUpdatingBranding: boolean;
    iconBranding: APITypes.organizationService.GetOrganizationPaymentIntegrationIconOrLogoResponse;
    isLoadingIconBranding: boolean;
    logoBranding: APITypes.organizationService.GetOrganizationPaymentIntegrationIconOrLogoResponse;
    isLoadingLogoBranding: boolean;
    isMissingBranding: boolean;
};

export default function useOrganizationPaymentIntegrationBranding({
    onUpdatedBranding,
}: UseOrganizationPaymentIntegrationBrandingArgs): UseOrganizationPaymentIntegrationBrandingReturnValue {
    const organizationId = useOrganizationId();
    const notify = useNotify();

    const iconBrandingQuery = useQuery({
        queryKey: getOrganizationPaymentIntegrationBrandingImageCacheKey('icon'),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationPaymentIntegrationIconOrLogoResponse>(
                urls.api.organizations.paymentIntegration.brandingImage(organizationId, 'icon'),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(iconBrandingQuery, intl('failedToLoadBranding'));

    const logoBrandingQuery = useQuery({
        queryKey: getOrganizationPaymentIntegrationBrandingImageCacheKey('logo'),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.GetOrganizationPaymentIntegrationIconOrLogoResponse>(
                urls.api.organizations.paymentIntegration.brandingImage(organizationId, 'logo'),
            );
        },
        staleTime: longTermCacheStaleTime,
    });
    useQueryErrorNotify(logoBrandingQuery, intl('failedToLoadBranding'));

    const updateBrandingMutation = useMutation({
        mutationFn: async (request: APITypes.organizationService.UpdateOrganizationPaymentIntegrationImagesRequest) => {
            return await appAPI.put<APITypes.organizationService.UpdateOrganizationPaymentIntegrationImagesResponse>(
                urls.api.organizations.paymentIntegration.brandingImages(organizationId),
                { body: request },
            );
        },
        onSuccess: (data) => {
            onUpdateOrganizationPaymentIntegrationBranding.resetCache({ organizationId })();
            if (data.icon === 'success' && data.logo === 'success') {
                notify('success', intl('successFullyUpdatedBranding'));
                onUpdatedBranding?.();
            } else {
                const targetMessage =
                    data.icon === 'success'
                        ? intl('logo')
                        : data.logo === 'success'
                        ? intl('icon')
                        : intl('iconAndLogo');
                notify('warn', intl('partialError', { target: targetMessage }));
            }
        },
        onError: () => {
            notify('error', intl('failedToUpdateBranding'));
        },
    });

    const isMissingBranding =
        !iconBrandingQuery.isLoading &&
        !logoBrandingQuery.isLoading &&
        (!iconBrandingQuery.data || !logoBrandingQuery.data);

    return {
        updateBranding: updateBrandingMutation.mutate,
        isUpdatingBranding: updateBrandingMutation.isPending,
        iconBranding: iconBrandingQuery.data || null,
        isLoadingIconBranding: iconBrandingQuery.isLoading,
        logoBranding: logoBrandingQuery.data || null,
        isLoadingLogoBranding: logoBrandingQuery.isLoading,
        isMissingBranding,
    };
}
